import type { IOptionalRuntimeRevisionReference } from '@edgebox/data-definition-kit';
import type { ClientContentRecommendationEntity } from '@edgebox/sync-core-rest-client';

/**
 * Reading the references off a page's suggested FAQs, and running the reads they
 * ask for a few at a time.
 */

/**
 * Any revision reference a suggestion carries. A suggestion references taxonomy
 * terms, content items and other suggestions alike, and everything here reads a
 * reference for its stable key, its revision id or the revision behind it and
 * never for the entity's own fields, so the referent type is left open; the
 * required and the optional reference both answer through these calls, so one
 * type takes either.
 */
export type SuggestionRevisionReference = IOptionalRuntimeRevisionReference<unknown>;

/** Runs the tasks a few at a time, so one tab opening cannot fire a request per dependency at once. */
export async function mapWithConcurrency<T>(values: T[], limit: number, run: (value: T) => Promise<void>): Promise<void> {
  let next = 0;

  const workers = Array.from({ length: Math.max(1, Math.min(limit, values.length)) }, async () => {
    while (next < values.length) {
      const index = next;
      next += 1;
      await run(values[index]);
    }
  });

  await Promise.all(workers);
}

/**
 * What the create call needs beside the suggestion. It is resolved once while
 * the tab loads, so adding a FAQ to the page is the one request the button
 * makes.
 */

export function collectTermKeys(suggestions: ClientContentRecommendationEntity[]): string[] {
  const keys = new Set<string>();
  const add = (reference: SuggestionRevisionReference | null | undefined) => {
    const key = reference?.getStableKey();
    if (key) {
      keys.add(key);
    }
  };

  for (const suggestion of suggestions) {
    add(suggestion.angle);
    add(suggestion.subject);
    add(suggestion.faqType);
    add(suggestion.initiative);
    (suggestion.terms ?? []).forEach(add);
    (suggestion.evidence?.gapSources ?? []).forEach(add);
    for (const entry of suggestion.evidence?.coverage ?? []) {
      add(entry?.engine);
      add(entry?.purpose);
    }
  }

  return [...keys];
}

/**
 * The page a suggestion says to answer it from is a content item, not a taxonomy
 * term, so it is read through the content-item service and named by the same
 * resolved-name map the badges read.
 */
export function collectPublishOnPageKeys(suggestions: ClientContentRecommendationEntity[]): string[] {
  const keys = new Set<string>();
  for (const suggestion of suggestions) {
    const key = suggestion.publishOn?.contentItem?.getStableKey();
    if (key) {
      keys.add(key);
    }
  }

  return [...keys];
}

/**
 * The customer's own pages an answer can be sourced from. They are content items
 * like the page a suggestion says to publish on, so they are read through the
 * content-item service and named — and addressed — by the same resolved map the
 * badges read.
 */
export function collectSourcePageKeys(suggestions: ClientContentRecommendationEntity[]): string[] {
  const keys = new Set<string>();
  for (const suggestion of suggestions) {
    for (const reference of suggestion.sources ?? []) {
      const key = reference?.getStableKey();
      if (key) {
        keys.add(key);
      }
    }
  }

  return [...keys];
}

/** A reference to another suggestion, as the entity's own `dependsOn` declares one. */
export type SuggestionDependencyReference = NonNullable<ClientContentRecommendationEntity['dependsOn']>[number];

/**
 * Each suggestion a page's suggestions wait on, once per stable key and still as
 * the reference that names it: what the row shows is read through that reference
 * rather than through a key copied out of it.
 */
export function collectDependsOn(
  suggestions: ClientContentRecommendationEntity[]
): Array<{ key: string; reference: SuggestionDependencyReference }> {
  const byKey = new Map<string, { key: string; reference: SuggestionDependencyReference }>();
  for (const suggestion of suggestions) {
    for (const reference of suggestion.dependsOn ?? []) {
      const key = reference?.getStableKey();
      if (key && reference.getRevisionId()) {
        byKey.set(key, { key, reference });
      }
    }
  }

  return [...byKey.values()];
}
